import React from 'react';
import {
  Route, createRoutesFromElements, createBrowserRouter, RouterProvider,
} from 'react-router';
import {
  NoAccessAccount, HealthCheck,
} from 'pages';
import Auth from 'app/auth/pages/Auth';
import { resource, types } from 'constants/accessControl';
import { lazyWithRetry } from 'utils/helpers';
import _ from 'lodash';
import { NavigationService } from 'services';
import BrandSettingLayout from 'layouts/BrandSettingLayout';
import BaseLayout from 'layouts/BaseLayout';
import { Can } from 'shared/permission';
import { googleCallbackLoader } from 'features/thirdPartyServices/components/analytics/loader';
import { brandRouters } from './brandRouters';
import { allRouters } from './allRouters';
import { PrivateRoute } from './PrivateRoute';
import { MultiRouteProps } from './types';

const renderMultiRoutes = ({ element, paths, ...rest }: MultiRouteProps) => paths.map((path: string) => (
  <Route key={path} path={path} {...rest} element={element} />
));

const routes = createRoutesFromElements(
  <Route>
    <Route path="/noAccessAccount" element={<NoAccessAccount />} />
    {renderMultiRoutes({
      paths: ['/forgotPassword', '/resendTFA', '/resetPassword', '/setPassword', '/login', '/emailSent'],
      element: <Auth />,
    })}
    <Route path="/health/check" element={<HealthCheck />} />
    <Route path="/brands/create" element={<BaseLayout routers={allRouters} />}>
      <Route
        index
        element={(
          <PrivateRoute
            id={0}
            menuParentId={16}
            component={lazyWithRetry(() => import('features/brands/pages/CreateBrand'))}
            hide={() => !!Can({
              type: types.add,
              rule: `${resource.brands}.${resource.brands}`,
              children: false,
              fallback: true,
            })}
            componentProps={{ partnerSettings: { viewMode: true } }}
            breadcrumb={[
              { name: 'home', path: '/' },
              { name: 'brands', path: '/brands' },
              { name: 'create' },
            ]}
          />
        )}
      />
    </Route>
    {/* Brand routes */}
    <Route path="/brands/:id" element={<BrandSettingLayout routers={brandRouters} page="brand-settings" />}>
      {_.tail(brandRouters).map(({ key, ...route }) => (
        <Route
          path={route.path}
          key={key}
          element={(
            <PrivateRoute
              {...route}
              componentProps={{ timeZoneSettings: route.timeZoneSettings }}
              hide={route.hide ?? (() => false)}
            />
          )}
        />
      ))}
    </Route>

    {/* General routes */}
    <Route path="/" element={<BaseLayout routers={allRouters} />}>
      <Route
        path="/"
        element={<PrivateRoute id={1} component={lazyWithRetry(() => import('features/home/pages/Account'))} hide={() => false} />}
      />
      {allRouters.map(({ key, ...route }) => (
        <Route
          path={route.path}
          key={key}
          element={(
            <PrivateRoute
              {...route}
              componentProps={{
                partnerSettings: route.partnerSettings,
                brandSettings: route.brandSettings,
                showConversion: route.showConversion,
                timeZoneSettings: route.timeZoneSettings,
              }}
              hide={route.hide ?? (() => false)}
            />
          )}
        />
      ))}
      <Route path="*" element={<PrivateRoute id={0} component={lazyWithRetry(() => import('pages/NoMatch'))} />} />
    </Route>

    <Route path="/googleAds/oauth/callback" loader={googleCallbackLoader} />
    <Route
      path="/bets/casino/:betId/round"
      element={(
        <PrivateRoute
          id={2000}
          component={lazyWithRetry(() => import('features/bets/pages/RoundDetails'))}
          hide={() => !!Can({
            type: types.view,
            rule: `${resource.bets}.${resource.casinoBets}`,
            children: false,
            fallback: true,
          })}
        />
      )}
    />
  </Route>,
);

const router = createBrowserRouter(routes);
NavigationService.setRouter(router);

const Routers = () => <RouterProvider router={router} />;

export default Routers;
